/**
 * Générateur — Automatisme : opposé d'un nombre, somme des opposés.
 * Attendu ID : 1092 | Niveau : 4ème | Domaine : Opérations sur les nombres relatifs
 * Rubrique : AUTOMATISME (programme 2025, cycle 4 page 9, ligne 633 :
 * « Opposé d'un nombre, somme des opposés. »).
 *
 * ── Cadrage (sous-agent programme-maths, brouillon 2026-09-12) ─────────────
 * Entretien de l'objectif 5e l. 523 (« définir l'opposé », gens 703/911).
 * Trois gestes, et rien d'autre : donner l'opposé (entier, décimal, 0, avec
 * la notation −(−3)) ; savoir que a + (−a) = 0 (la « somme des opposés ») ;
 * reconnaître deux opposés. Pourquoi en 4e : « multiplier par −1 revient à
 * prendre l'opposé » et « la notion d'opposé peut être utilisée pour
 * soustraire » (Eduscol 4e 2025).
 * Frontières : opposé d'une fraction (objectif 4e l. 662), inverse (objectif
 * l. 665 : en septembre, « 1/3 » n'est pas encore une erreur possible, il
 * n'est pas en distracteur ; choix mode auto, À ARBITRER), valeur absolue
 * notée |a| (jamais, comme 703), opposé d'une expression (3e).
 *
 * Périmètre observé MathsMentales (page 4e ; S. Cogez, Apache 2.0, relevé
 * 2026-09-12) : seule la leçon 5LN2 (« nombres dont la somme vaut zéro ») est
 * retenue, sans activité ; 5NA2 (opposé d'un entier −100..100, d'un décimal,
 * d'une fraction) rattachée sans options. Rien sur −(−a) ni sur la somme des
 * opposés : les cartes viennent du programme.
 *
 * ── Plan de cartes ─────────────────────────────────────────────────────────
 *   Carte 1 « Série flash » (flash_texte_trou_saisie) : quatre lignes en
 *     texte, une par écriture : « L'opposé de -7 est … » (nombre négatif, le
 *     cas discriminant) ; « L'opposé de 12,5 est … » (positif, décimal une
 *     fois sur deux, 0 une fois sur six) ; « -(-9) = … » (la notation) ;
 *     « … + 15 = 0 » ou « 6 + … = 0 » (la somme des opposés, trou sur
 *     l'opposé). Tiret clavier, virgule décimale ; « +7 » accepté par la
 *     tolérance numérique.
 *   Carte 2 « L'opposé, c'est lequel ? » (opposé_qcm_unique) : « Quel est
 *     l'opposé de -7 ? » ou « Quel nombre ajouter à -7 pour obtenir 0 ? »
 *     (les deux moitiés de la ligne), TROIS choix : la clé, le nombre lui-même
 *     (signe_garde : « je mets un moins devant » ; distance_a_zero pour un
 *     positif) et 0 (zero_confondu : « l'opposé, ça fait zéro »). Un choix à
 *     quatre exigerait l'inverse ou une valeur inventée : refusé.
 *   Carte 3 « Vrai ou faux » (sens_tableau_vf) : trois lignes tirées parmi
 *     six gabarits (a + (−a) = 0 ; −(−a) = −a ; l'opposé de 0 est 0 ; −a + a
 *     = −2a ; l'opposé de a est −a ; a et a sont opposés), une ou deux vraies,
 *     chaque fausse portant un code d'erreur.
 *
 * INVARIANTS : arithmétique en DIXIÈMES entiers (zéro flottant) ; entiers
 * −50..50, décimaux −20..20 à une décimale non nulle ; chaque ligne relue
 * depuis son texte et confrontée à la valeur (throw).
 * SACRIFIÉ (fiche d'aide à venir, « −6 et 6 à la même distance de 0 ») :
 * les nombres 6 et −6 ne sortent d'aucune carte (invariant auto-testé).
 */
import {
    randomChoice, randInt, shuffle,
    makeQcm, makeVraiFaux, makeTexteTrou,
} from '../../../utils';

const ATTENDU_ID = 1092;
const opts = (label) => ({ niveau: '4eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_FLASH = 'Série flash';
const L_QCM = 'L\'opposé, c\'est lequel ?';
const L_VF = 'Vrai ou faux';

const MAX_ENTIER = 50;  // entiers −50..50
const MAX_DECIMAL = 20; // décimaux −20..20 (partie entière)

// ─── Le domaine (dixièmes entiers) ────────────────────────────────────────

/** Le nombre de la fiche d'aide, sacrifié avec son opposé. */
export const SACRIFIE = 6;
const estSacrifie = (d) => Math.abs(d) === SACRIFIE * 10;

/** dixièmes → écriture française : « -12,5 », « 7 », « 0 ». */
const fr = (d) => {
    const s = d < 0 ? '-' : '';
    const a = Math.abs(d);
    return s + (a % 10 === 0 ? String(a / 10) : `${Math.floor(a / 10)},${a % 10}`);
};

/** Dans une écriture : un négatif est entre parenthèses quand il suit un signe. */
const paren = (d) => (d < 0 ? `(${fr(d)})` : fr(d));

/** « -12,5 » → dixièmes (le second chemin de l'assert). */
const relire = (texte) => {
    const m = String(texte).match(/^(-?)(\d+)(?:,(\d))?$/);
    if (!m) throw new Error(`gen_1092 : écriture illisible ${texte}`);
    const v = parseInt(m[2], 10) * 10 + (m[3] ? parseInt(m[3], 10) : 0);
    return m[1] === '-' ? -v : v;
};

const verifier = (d) => {
    if (!Number.isInteger(d) || relire(fr(d)) !== d) throw new Error(`gen_1092 : valeur ${d}`);
    if (estSacrifie(d)) throw new Error('gen_1092 : nombre sacrifié tiré');
    const entierOk = d % 10 === 0 && Math.abs(d) <= MAX_ENTIER * 10;
    const decimalOk = d % 10 !== 0 && Math.abs(d) <= MAX_DECIMAL * 10 + 9;
    if (!entierOk && !decimalOk) throw new Error(`gen_1092 : hors domaine ${d}`);
    return d;
};

/** Un entier non nul (−50..50) ou un décimal à une décimale non nulle (−20..20), du signe demandé, jamais 6 ni −6. */
const tirer = ({ decimal = randomChoice([true, false]), signe = randomChoice([1, -1]) } = {}) => {
    for (let essai = 0; essai < 60; essai++) {
        const v = decimal ? randInt(0, MAX_DECIMAL - 1) * 10 + randInt(1, 9) : randInt(1, MAX_ENTIER) * 10;
        if (!estSacrifie(v)) return verifier(signe * v);
    }
    return verifier(signe * (decimal ? 125 : 70));
};

// ─── Explications ─────────────────────────────────────────────────────────

const EXPLICATIONS = {
    signe_garde: 'L\'opposé change le signe : l\'opposé d\'un nombre négatif est positif.',
    distance_a_zero: 'L\'opposé de 5 n\'est pas 5 : c\'est le nombre à la même distance de zéro, de l\'autre côté.',
    zero_confondu: 'C\'est la SOMME d\'un nombre et de son opposé qui vaut 0, pas l\'opposé.',
    distances_ajoutees: 'Un nombre plus son opposé, ça fait 0 : ils s\'annulent.',
    egaux_pas_opposes: 'Deux nombres égaux ne sont pas opposés : il faut un signe de chaque côté de zéro.',
    regle: 'Deux nombres opposés sont à la même distance de zéro, de part et d\'autre. Leur somme vaut 0, et l\'opposé de l\'opposé est le nombre lui-même.',
};

// ─── Codes d'erreur (contenu.erreurs, codes communs du catalogue) ─────────

/** Le code commun d'un code du gen : signe_garde, distance_a_zero et egaux_pas_opposes ont été fusionnés ; distances_ajoutees (−a + a = −2a) a son propre code depuis le lot 2 (04/10/2026, docs/SFD_CODES_ERREUR.md). */
const CODE_COMMUN = { signe_garde: 'oppose_sans_changer_signe', distance_a_zero: 'oppose_sans_changer_signe', egaux_pas_opposes: 'oppose_sans_changer_signe', distances_ajoutees: 'somme_des_opposes_doublee' };
const commun = (code) => CODE_COMMUN[code] || code;

/** Les erreurs d'un trou qui demande l'opposé de x (en dixièmes) : x lui-même, ou 0 ; rien pour x = 0. */
const erreursOppose = (x, trou) => (x === 0 ? [] : [
    { code: commun('signe_garde'), trou, valeur: fr(x) },
    { code: 'zero_confondu', trou, valeur: '0' },
]);

/** Une erreur par ligne fausse du Vrai/Faux, avec son code. */
const erreursVf = (items) => items.filter((it) => !it.vrai && it.code).map((it) => ({ code: commun(it.code), ligne: it.ligne }));

// ─── Carte 1 : Série flash (4 lignes saisie) ──────────────────────────────

const CONSIGNE_FLASH = '<p><b>Complète.</b> Un nombre négatif s\'écrit avec le tiret (ex. : -8), un décimal avec la virgule.</p>';

const genFlash = () => {
    // 1. l'opposé d'un négatif (entier ou décimal)
    const a = tirer({ signe: -1 });
    // 2. l'opposé d'un positif ; 0 une fois sur six (l'opposé de 0 est 0)
    const b = randInt(1, 6) === 1 ? 0 : tirer({ signe: 1 });
    // 3. la notation −(−c) : entier
    const c = tirer({ decimal: false, signe: -1 });
    // 4. la somme des opposés, trou devant ou derrière
    const d = tirer();
    const trouDevant = randomChoice([true, false]);
    const lignes = [
        `L'opposé de ${fr(a)} est {{${fr(-a)}}}`,
        `L'opposé de ${fr(b)} est {{${fr(-b)}}}`,
        `-(${fr(c)}) = {{${fr(-c)}}}`,
        trouDevant ? `{{${fr(-d)}}} + ${paren(d)} = 0` : `${fr(d)} + {{${fr(-d)}}} = 0`,
    ];
    const payload = makeTexteTrou(lignes.join('\n'), CONSIGNE_FLASH, { ...opts(L_FLASH), mode: 'saisie' });
    payload.contenu = { ...payload.contenu, explications: { regle: EXPLICATIONS.regle } };
    payload._meta.params = { a, b, c, d, trouDevant };
    payload.contenu = { ...payload.contenu, erreurs: [a, b, c, d].flatMap((x, trou) => erreursOppose(x, trou)) };
    return payload;
};

// ─── Carte 2 : QCM « L'opposé, c'est lequel ? » ───────────────────────────

const genQcm = () => {
    const x = tirer();
    const question = randomChoice(['oppose', 'somme']);
    const texte = question === 'oppose'
        ? `<p><b>Quel est l'opposé de ${fr(x)} ?</b></p>`
        : `<p><b>Quel nombre faut-il ajouter à ${fr(x)} pour obtenir 0 ?</b></p>`;
    const bon = fr(-x);
    const faux = [
        { valeur: fr(x), code: x < 0 ? 'signe_garde' : 'distance_a_zero' },
        { valeur: '0', code: 'zero_confondu' },
    ];
    if (new Set([bon, ...faux.map((f) => f.valeur)]).size !== 3) throw new Error('gen_1092 : choix en collision');
    const payload = makeQcm([bon, ...faux.map((f) => f.valeur)], bon, texte, opts(L_QCM), false);
    payload.contenu = { ...payload.contenu, explications: EXPLICATIONS };
    payload._meta.params = { x, question, distracteurs: faux.map(({ code }) => ({ code })) };
    payload.contenu = { ...payload.contenu, erreurs: faux.map((f) => ({ code: commun(f.code), valeur: f.valeur })) };
    return payload;
};

// ─── Carte 3 : Vrai ou faux (3 lignes) ────────────────────────────────────

/** Les gabarits : chacun rend { ligne, vrai, code } pour un nombre tiré. */
const GABARITS = {
    somme_nulle: () => { const a = tirer(); return { ligne: `${fr(a)} + ${paren(-a)} = 0`, vrai: true }; },
    double_moins_faux: () => { const a = tirer({ decimal: false, signe: 1 }); return { ligne: `-(-${fr(a)}) = -${fr(a)}`, vrai: false, code: 'signe_garde' }; },
    zero: () => ({ ligne: 'L\'opposé de 0 est 0.', vrai: true }),
    somme_doublee: () => {
        // −a + a = −2a : le double ne doit pas être le nombre sacrifié (a = 3 → −6)
        let a = tirer({ signe: 1 });
        while (estSacrifie(2 * a)) a = tirer({ signe: 1 });
        return { ligne: `-${fr(a)} + ${fr(a)} = -${fr(2 * a)}`, vrai: false, code: 'distances_ajoutees' };
    },
    oppose_juste: () => { const a = tirer(); return { ligne: `L'opposé de ${fr(a)} est ${fr(-a)}.`, vrai: true }; },
    egaux: () => { const a = tirer(); return { ligne: `${fr(a)} et ${fr(a)} sont opposés.`, vrai: false, code: 'egaux_pas_opposes' }; },
};

const genVf = () => {
    for (let essai = 0; essai < 80; essai++) {
        const noms = shuffle(Object.keys(GABARITS)).slice(0, 3);
        const items = noms.map((nom) => ({ nom, ...GABARITS[nom]() }));
        const nbVraies = items.filter((it) => it.vrai).length;
        if (nbVraies < 1 || nbVraies > 2) continue;
        if (new Set(items.map((it) => it.ligne)).size < 3) continue;
        const payload = makeVraiFaux(items.map((it) => it.ligne), items.map((it) => (it.vrai ? 0 : 1)), '<p><b>Vrai ou faux ?</b></p>', opts(L_VF));
        payload.contenu = { ...payload.contenu, explications: EXPLICATIONS };
        payload._meta.params = { items: items.map(({ nom, vrai, code }) => ({ nom, vrai, ...(code ? { code } : {}) })) };
        payload.contenu = { ...payload.contenu, erreurs: erreursVf(items) };
        return payload;
    }
    const items = [
        { nom: 'somme_nulle', ligne: '7 + (-7) = 0', vrai: true },
        { nom: 'double_moins_faux', ligne: '-(-9) = -9', vrai: false, code: 'signe_garde' },
        { nom: 'zero', ligne: 'L\'opposé de 0 est 0.', vrai: true },
    ];
    const payload = makeVraiFaux(items.map((it) => it.ligne), [0, 1, 0], '<p><b>Vrai ou faux ?</b></p>', opts(L_VF));
    payload.contenu = { ...payload.contenu, explications: EXPLICATIONS };
    payload._meta.params = { items: items.map(({ nom, vrai, code }) => ({ nom, vrai, ...(code ? { code } : {}) })) };
    payload.contenu = { ...payload.contenu, erreurs: erreursVf(items) };
    return payload;
};

// ─── Contrat distracteurs (référence de revue) ────────────────────────────

export const REGLES_DISTRACTEURS_QCM = {
    invariants_communs: [
        'trois choix : la clé, le nombre lui-même, 0 ; distincts par construction (x ≠ 0)',
        'jamais l\'inverse (objectif de l\'année, l. 665 : mort en septembre), jamais de valeur inventée',
        'le nombre sacrifié 6 (et −6) ne sort d\'aucune carte',
    ],
    qcm_unique: {
        support: 'un entier −50..50 ou un décimal −20..20 à une décimale, non nul ; question « l\'opposé de x » ou « quel nombre ajouter à x pour obtenir 0 »',
        familles: {
            signe_garde: 'x lui-même quand x < 0 : « je mets un moins devant » (l\'opposé de −7 → −7)',
            distance_a_zero: 'x lui-même quand x > 0 : l\'opposé de 5 → 5',
            zero_confondu: '0 : la somme des opposés prise pour l\'opposé',
        },
    },
    vf_tableau_vf: 'trois gabarits sur six, une ou deux vraies ; fausses : −(−a) = −a (signe_garde), −a + a = −2a (distances_ajoutees), a et a opposés (egaux_pas_opposes)',
};

// ─── Registre et point d'entrée ───────────────────────────────────────────

const GENERATORS = {
    flash_texte_trou_saisie: genFlash,
    oppose_qcm_unique: genQcm,
    sens_tableau_vf: genVf,
};

// etapes : nombre d'étapes de raisonnement de la carte, validé par le prof le
// 2026-10-06 (chantier arbres-4e) ; sert aux exercices complexes du groupe Acquis.
export const CATEGORIES = [
    { id: 'flash', label: L_FLASH, etapes: 1, formatsList: [{ key: 'flash_texte_trou_saisie', label: 'Saisie trous' }] },
    { id: 'oppose', label: L_QCM, etapes: 1, formatsList: [{ key: 'oppose_qcm_unique', label: 'QCM' }] },
    { id: 'sens', label: L_VF, etapes: 1, formatsList: [{ key: 'sens_tableau_vf', label: 'Vrai/Faux' }] },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
